import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Line = styled.div`
	margin-bottom: 4px;

	&:last-child {
		margin-bottom: 0;
	}
`;

const Speaker = styled.span`
	font-weight: 600;
	font-style: normal;
`;

const Content = styled.span`
	font-style: normal;
`;

const Break = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 8px 0;
	font-size: 12px;
	opacity: 0.6;

	&::before,
	&::after {
		content: '';
		flex: 1;
		border-top: 1px solid currentColor;
		opacity: 0.4;
	}
`;

const TRANSCRIPT_BREAK = '<BREAK>';

export type TranscriptionSpeakerInfo = {
	speakerNumber: number;
	/** The part between <BREAK> lines this applies to; null applies to the whole text. */
	segmentIndex?: number | null;
	user: {
		id: string;
		firstName: string;
		lastName: string;
	};
};

type Props = {
	text: string;
	speakers?: TranscriptionSpeakerInfo[] | null;
};

function speakerNamesForSegment(
	speakers: TranscriptionSpeakerInfo[] | null | undefined,
	segmentIndex: number,
): Map<string, string> | null {
	if (!speakers?.length) return null;

	const map = new Map<string, string>();

	for (const speaker of speakers) {
		if (speaker.segmentIndex != null && speaker.segmentIndex !== segmentIndex) continue;

		const label = `Speaker ${speaker.speakerNumber}`;
		if (speaker.segmentIndex == null && map.has(label)) continue;

		map.set(label, `${speaker.user.firstName} ${speaker.user.lastName}`);
	}

	return map;
}

export const FormattedTranscript = ({ text, speakers }: Props) => {
	const { t } = useTranslation();

	const segments = useMemo(() => {
		const parts: string[][] = [[]];

		for (const line of text.split('\n')) {
			if (line.trim() === TRANSCRIPT_BREAK) parts.push([]);
			else parts[parts.length - 1].push(line);
		}

		return parts.map((lines, index) => ({
			lines,
			names: speakerNamesForSegment(speakers, index),
		}));
	}, [text, speakers]);

	return (
		<div>
			{segments.map(({ lines, names }, segmentIndex) => (
				<Fragment key={segmentIndex}>
					{segmentIndex > 0 && <Break>{t('transcription.streaming.break')}</Break>}
					{lines.map((line, i) => {
						const colonIndex = line.indexOf(': ');
						if (colonIndex === -1) {
							return <Line key={i}>{line}</Line>;
						}

						const rawSpeaker = line.slice(0, colonIndex);
						const content = line.slice(colonIndex + 2);
						const speaker = names?.get(rawSpeaker) ?? rawSpeaker;

						return (
							<Line key={i}>
								<Speaker>{speaker}:</Speaker> <Content>{content}</Content>
							</Line>
						);
					})}
				</Fragment>
			))}
		</div>
	);
};
